import { useState } from 'react'
import { fcfa } from '../api/format'
import { prixDuPalier } from '../api/mrr'
import { resumeAccesClient, validerClient } from '../api/client'
import { ChampTelephone, PAYS_PAR_DEFAUT, afficherTelephone, useTelephone } from '../api/commun'
import { MESSAGE_COMPTE_EXISTANT_FONCTION } from '../api/identifiants'
import { supabase } from '../api/supabase'
import { LIBELLE_PALIER, PALIERS } from '../api/abonnement'
import type { FormulaireClient, IdentifiantsClient, Plan, Source } from '../api/types'
import { Icone } from './Icone'
import { Modale } from './Modale'

const VIDE: FormulaireClient = {
  name: '', contact_name: '', email: '', telephone: { texte: '', iso: PAYS_PAR_DEFAUT }, quartier: '', commune: '',
  plan_tier: 'starter', brand_color: '#7C3AED',
}

/**
 * « Nouveau client » — l'autre acte d'écriture de la console. Tout se passe
 * côté serveur (edge function `create-tenant`) ; les accès sont affichés UNE
 * SEULE FOIS : le mot de passe temporaire n'est stocké nulle part.
 */
export function ModaleClient({
  plans,
  source,
  surFermer,
  surCree,
  notifier,
}: {
  plans: Plan[] | null
  source: Source
  surFermer: () => void
  surCree: () => void
  notifier: (m: string) => void
}) {
  const [fBase, setFBase] = useState<Omit<FormulaireClient, 'telephone'>>(VIDE)
  const [erreur, setErreur] = useState('')
  const [enCours, setEnCours] = useState(false)
  const [acces, setAcces] = useState<IdentifiantsClient | null>(null)

  /* Le pays de départ du champ « numéro » vient de la configuration (pays de l'organisation opératrice). */
  const tel = useTelephone(supabase)
  const f = { ...fBase, telephone: tel.valeur }

  const maj = <K extends keyof Omit<FormulaireClient, 'telephone'>>(k: K, v: FormulaireClient[K]) => setFBase((x) => ({ ...x, [k]: v }))

  async function creer() {
    const refus = validerClient(f)
    if (refus) { setErreur(refus); return }
    setEnCours(true)
    setErreur('')
    try {
      setAcces(await source.creerClient(f))
      notifier(`Client « ${f.name.trim()} » créé`)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Création refusée : réessayez.')
    } finally {
      setEnCours(false)
    }
  }

  async function copier() {
    if (!acces) return
    const r = resumeAccesClient(acces)
    const t = `Portail : https://deliver.yiriwa-group.com\n${r.identifiants.map((i) => `Identifiant : ${i}\n`).join('')}`
      + (r.secret ? `Mot de passe temporaire : ${r.secret}\n` : `${r.phrase}\n`)
      + `Code équipe livreurs : ${acces.joinCode}`
    try {
      await navigator.clipboard.writeText(t)
      notifier('Accès copiés')
    } catch {
      notifier('Copie impossible')
    }
  }

  const libelleOffre = (t: (typeof PALIERS)[number]) => {
    const p = plans ? prixDuPalier(plans, t) : null
    return `${LIBELLE_PALIER[t]}${plans ? (p === null ? ' — sur devis' : ` — ${fcfa(p)}`) : ''}`
  }

  if (acces) {
    const resume = resumeAccesClient(acces)
    return (
      <Modale
        titre="Compte créé"
        surFermer={() => { surCree(); surFermer() }}
        pied={<button type="button" className="btn btn-plein" onClick={() => { surCree(); surFermer() }} data-focus-initial>Terminé</button>}
      >
        {resume.secret
          ? <p className="encart"><Icone nom="alert" /> Transmettez ces accès au gérant. <b>Ils ne seront plus affichés.</b></p>
          : <p className="encart"><Icone nom="alert" /> {acces.connexion === 'whatsapp' ? resume.phrase : MESSAGE_COMPTE_EXISTANT_FONCTION}</p>}
        <dl className="liste-def">
          <div><dt>Portail</dt><dd>deliver.yiriwa-group.com</dd></div>
          {acces.email && <div><dt>E-mail</dt><dd>{acces.email}</dd></div>}
          {acces.phone && <div><dt>Numéro WhatsApp</dt><dd>{afficherTelephone(acces.phone)}</dd></div>}
          {resume.secret && <div><dt>Mot de passe temporaire</dt><dd className="n">{resume.secret}</dd></div>}
          <div><dt>Code équipe livreurs</dt><dd className="n">{acces.joinCode}</dd></div>
        </dl>
        {acces.avertissement && <p className="erreur" role="alert">{acces.avertissement}</p>}
        <button type="button" className="btn btn-doux" onClick={copier}><Icone nom="copy" /> Copier les accès</button>
      </Modale>
    )
  }

  return (
    <Modale
      titre="Nouveau client SaaS"
      surFermer={surFermer}
      pied={
        <>
          <button type="button" className="btn btn-doux" onClick={surFermer} disabled={enCours}>Annuler</button>
          <button type="button" className="btn btn-plein" onClick={creer} disabled={enCours}>
            {enCours ? 'Création…' : 'Créer le compte client'}
          </button>
        </>
      }
    >
      <form onSubmit={(e) => { e.preventDefault(); void creer() }} noValidate>
        <div className="ligne2">
          <div className="champ">
            <label htmlFor="c-nom">Raison sociale <b aria-hidden="true">*</b></label>
            <input id="c-nom" placeholder="Bolt Logistics SARL" value={f.name} onChange={(e) => maj('name', e.target.value)} required />
          </div>
          <div className="champ">
            <label htmlFor="c-plan">Palier de départ</label>
            <select id="c-plan" value={f.plan_tier} onChange={(e) => maj('plan_tier', e.target.value as FormulaireClient['plan_tier'])}>
              {PALIERS.map((t) => <option key={t} value={t}>{libelleOffre(t)}</option>)}
            </select>
          </div>
        </div>
        <div className="ligne2">
          <div className="champ">
            <label htmlFor="c-contact">Gérant <b aria-hidden="true">*</b></label>
            <input id="c-contact" placeholder="Fatou Coulibaly" value={f.contact_name} onChange={(e) => maj('contact_name', e.target.value)} required />
          </div>
          <div className="champ">
            <label htmlFor="c-mail">E-mail du gérant (recommandé)</label>
            <input id="c-mail" type="email" placeholder="gerant@entreprise.ml" value={f.email} onChange={(e) => maj('email', e.target.value)} />
          </div>
        </div>
        <div className="champ">
          <ChampTelephone
            valeur={tel.valeur}
            surChangement={tel.setValeur}
            etiquette="Numéro WhatsApp du gérant"
            montrerErreur={erreur !== ''}
            id="c-tel"
          />
          <span className="aide">Au moins un des deux : e-mail ou numéro WhatsApp. Avec un e-mail, un gérant sans compte reçoit un mot de passe temporaire ; avec un numéro seul, il se connecte avec un code reçu sur WhatsApp. Le client démarre par un essai de 30 jours.</span>
        </div>
        <div className="ligne2">
          <div className="champ">
            <label htmlFor="c-quartier">Quartier</label>
            <input id="c-quartier" placeholder="Hamdallaye ACI 2000" value={f.quartier} onChange={(e) => maj('quartier', e.target.value)} />
          </div>
          <div className="champ">
            <label htmlFor="c-commune">Commune</label>
            <input id="c-commune" placeholder="Commune IV" value={f.commune} onChange={(e) => maj('commune', e.target.value)} />
          </div>
        </div>
        <div className="champ">
          <label htmlFor="c-couleur">Couleur de marque</label>
          <div className="ligne-couleur">
            <input id="c-couleur" type="color" value={f.brand_color} onChange={(e) => maj('brand_color', e.target.value)} />
            <span className="aide">Accent du portail et de l'app livreur du client.</span>
          </div>
        </div>
        <p className="erreur" role="alert" aria-live="assertive">{erreur}</p>
      </form>
    </Modale>
  )
}
